import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { toast } from "react-toastify";
import { setCart } from "../Redux/CartSlice";
import {
  getCartByUserId,
  updateCartOnServer,
  deleteCartOnServer,
} from "../services/Cart/CartPage";
import LoginPopup from "../component/common/LoginPopup";

export const useCartActions = () => {
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart.items || []);

  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const userId =
    JSON.parse(localStorage.getItem("medarphaUser") || "{}")?.userId;

  // ================= HELPERS =================
  const getStoreId = (item) =>
    Number(item?.StoreId ?? item?.storeId ?? 0);

  // ================= STORE CHECK FIXED =================
  const hasDifferentStore = (newStoreId) => {
    const normalizedNewStoreId = Number(newStoreId);

    const existingStoreIds = cart
      .map(getStoreId)
      .filter(Boolean);

    return (
      existingStoreIds.length > 0 &&
      !existingStoreIds.includes(normalizedNewStoreId)
    );
  };

  // ================= REFRESH =================
  const refreshCart = async () => {
    if (!userId) return;

    try {
      const res = await getCartByUserId(userId);

      const items =
        res?.items ||
        res?.data?.items ||
        res?.data ||
        [];

      dispatch(setCart(items));

      const cartId = res?.cartId || res?.data?.cartId;

      if (cartId) {
        localStorage.setItem("cartId", cartId);
        console.log("🟢 CART ID SAVED:", cartId);
      }
    } catch (err) {
      console.error("Refresh cart error:", err);
    }
  };

  // ================= CLEAR CART =================
  const clearCart = async () => {
    const cartId = localStorage.getItem("cartId");

    console.log("🟡 CLEAR CART CALLED");
    console.log("🟡 CART ID:", cartId);

    if (!cartId || cartId === "0") {
      dispatch(setCart([]));
      toast.warn("Cart already empty");
      return;
    }

    try {
      await deleteCartOnServer(cartId);
      toast.success("Cart cleared successfully");
    } catch (error) {
      console.error("❌ DELETE CART ERROR:", error);
      toast.error("Failed to clear cart");
    }

    dispatch(setCart([]));
    localStorage.removeItem("cartId");

    console.log("🧹 LOCAL CART CLEARED");
  };

  // ================= ADD =================
  const addWithReplaceCheck = async (item) => {
    if (!userId) {
      setShowLoginPopup(true);
      toast.warn("Please login first");
      return;
    }

    const itemStoreId = getStoreId(item);

    if (hasDifferentStore(itemStoreId)) {
      const ok = window.confirm(
        "Different lab detected. Replace cart?"
      );

      if (!ok) return;

      toast.info("Replacing cart...");
      await clearCart();
      await new Promise((r) => setTimeout(r, 300));
    }

    try {
      await updateCartOnServer({
        userId,
        productId: item.productId,
        categoryId: item.categoryId,
        totalQuantity: 1,
        price: item.price,
        discount: 0,
        coupon: "",
      });

      await refreshCart();

      toast.success(
        `${item.name || item.productName || "Item"} added to cart`
      );
    } catch (error) {
      console.error("Add error:", error);
      toast.error("Failed to add item");
    }
  };

  // ================= REMOVE =================
  const removeItem = async (item) => {
    if (!userId) {
      setShowLoginPopup(true);
      toast.warn("Please login first");
      return;
    }

    try {
      await updateCartOnServer({
        userId,
        productId: item.productId,
        categoryId: item.categoryId,
        totalQuantity: -1,
        price: item.price,
        discount: 0,
        coupon: "",
      });

      await refreshCart();

      toast.info(
        `${item.name || item.productName || "Item"} removed from cart`
      );
    } catch (error) {
      console.error("Remove error:", error);
      toast.error("Failed to remove item");
    }
  };

  // ================= POPUP =================
  const LoginPopupComponent = (
    <LoginPopup
      isOpen={showLoginPopup}
      onClose={() => setShowLoginPopup(false)}
    />
  );

  return {
    addWithReplaceCheck,
    removeItem,
    refreshCart,
    clearCart,
    hasDifferentStore,
    LoginPopupComponent,
  };
};